import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import { useAuthActions, useConvexAuth } from "@convex-dev/auth/react";
import { useMutation, useQuery_experimental as useQuery } from "convex/react";

import { api } from "../../../../convex/_generated/api";
import { AppShell, PublicFrame } from "../components/shell";
import { Button, Notice, Spinner } from "../components/ui";
import { getErrorCode, isConnectionError } from "../lib/errors";
import { SignIn } from "./sign-in";

const OPERATOR_QUERY_TIMEOUT_MS = 10_000;

function AccessDenied({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <PublicFrame>
      <div className="state-panel state-panel--denied">
        <p className="eyebrow">Access denied</p>
        <h1>This account is not the Packful operator</h1>
        <p>
          No organisation information has been loaded. Sign out and use the
          verified operator email, or contact the Packful owner.
        </p>
        <Button onClick={() => void onSignOut()}>Sign out</Button>
      </div>
    </PublicFrame>
  );
}

function LoadingAccess({ label }: { label: string }) {
  return (
    <PublicFrame>
      <div className="state-panel state-panel--loading">
        <Spinner label={label} />
        <p>{label}</p>
      </div>
    </PublicFrame>
  );
}

function AccessFailure({ onRetry }: { onRetry: () => void }) {
  return (
    <PublicFrame>
      <div className="state-panel">
        <p className="eyebrow">Connection interrupted</p>
        <h1>Operator access could not be checked</h1>
        <Notice tone="danger">
          Could not reach Packful. Check your connection and try again.
        </Notice>
        <Button onClick={onRetry}>Try again</Button>
      </div>
    </PublicFrame>
  );
}

function PendingOperatorAccess({ onRetry }: { onRetry: () => void }) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setTimedOut(true);
    }, OPERATOR_QUERY_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, []);

  return timedOut ? (
    <AccessFailure onRetry={onRetry} />
  ) : (
    <LoadingAccess label="Checking operator access…" />
  );
}

export function OperatorGate({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { signOut } = useAuthActions();
  const bindCurrentIdentity = useMutation(api.operators.bindCurrentIdentity);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [bindingError, setBindingError] = useState<unknown>(null);
  const bindingInFlight = useRef(false);
  const bindingAttempt = useRef<number | null>(null);
  const currentOperator = useQuery({
    query: api.operators.getCurrent,
    args: isAuthenticated ? {} : "skip",
    throwOnError: false,
  });

  useEffect(() => {
    if (
      !isAuthenticated ||
      currentOperator.status !== "success" ||
      currentOperator.data !== null ||
      bindingInFlight.current ||
      bindingAttempt.current === retryAttempt
    ) {
      return;
    }

    bindingAttempt.current = retryAttempt;
    bindingInFlight.current = true;
    setBindingError(null);
    void bindCurrentIdentity({})
      .catch(setBindingError)
      .finally(() => {
        bindingInFlight.current = false;
      });
  }, [bindCurrentIdentity, currentOperator, isAuthenticated, retryAttempt]);

  function retry() {
    bindingAttempt.current = null;
    setBindingError(null);
    setRetryAttempt((current) => current + 1);
  }

  if (isLoading) {
    return <LoadingAccess label="Restoring secure session…" />;
  }
  if (!isAuthenticated) {
    return (
      <PublicFrame>
        <SignIn />
      </PublicFrame>
    );
  }
  if (currentOperator.status === "pending") {
    return <PendingOperatorAccess key={retryAttempt} onRetry={retry} />;
  }
  if (currentOperator.status === "error") {
    return <AccessFailure onRetry={() => window.location.reload()} />;
  }
  if (currentOperator.data) {
    return (
      <AppShell operatorEmail={currentOperator.data.email} onSignOut={signOut}>
        {children}
      </AppShell>
    );
  }
  if (bindingError) {
    const code = getErrorCode(bindingError);
    if (
      code === "NOT_OPERATOR" ||
      code === "OPERATOR_ALREADY_BOUND" ||
      code === "FORBIDDEN"
    ) {
      return <AccessDenied onSignOut={signOut} />;
    }
    if (isConnectionError(bindingError)) {
      return <AccessFailure onRetry={retry} />;
    }
    return <AccessFailure onRetry={retry} />;
  }
  return <LoadingAccess label="Binding verified operator identity…" />;
}
